import { Component, inject, signal } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { SharedModule } from "src/shared/shared.module";
import { ToastService } from "src/app/services/toast-service/toast.service";
import { CommonModule } from "@angular/common";
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from "@angular/forms";
import { Router, RouterModule } from "@angular/router";
import { merge, Observable, debounceTime, forkJoin, lastValueFrom } from "rxjs";
import { CourseService } from "src/app/services/api/courses.service";
import { LabelMode } from "src/utils/label-mode.enum";
import { AuthService } from "../auth";
import { BlogItemComponent } from "./components/blog-item/blog-item.component";
import { BlogService } from "src/app/services/api/blog.service";
import { Blog } from "src/app/services/model/blog.model";
import moment from "moment";
import { BlogCategory } from "src/app/services/model/blog-category.model";

@Component({
  selector: "app-blog",
  imports: [
    SharedModule,
    CommonModule,
    RouterModule,
    ReactiveFormsModule,
    TranslateModule,
    BlogItemComponent,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: "./blog.component.html",
  styleUrl: "./blog.component.scss",
})
export class BlogComponent {
  courseItems = [1, 2, 3, 4];

  isLoading = signal(true);
  tableLoading = signal(true);

  courseCategories: any = [];
  blogs: Blog[] = [];
  sortOptions = [
    {
      id: 1,
      name: "คอร์สขายดี",
      value: "best_seller desc",
    },
    {
      id: 2,
      name: "รีวิวสูงสุด",
      value: "total_review desc",
    },
    {
      id: 3,
      name: "อัปเดตล่าสุด",
      value: "updated_at desc",
    },
  ];
  dataSource: any;
  columns: any;
  form: FormGroup;
  blogCategories: BlogCategory[];

  #courseService = inject(CourseService);
  #blogService = inject(BlogService);
  #authService = inject(AuthService);
  #router = inject(Router);
  #fb = inject(FormBuilder);
  #toastService = inject(ToastService);
  #translate = inject(TranslateService);

  LabelMode = LabelMode;

  ngOnInit(): void {
    this.buildForm();
    this.getMasterData();
  }

  buildForm() {
    this.form = this.#fb.group({
      datatable: true,
      page: 1,
      order: "created_at desc",
      limit: 10,
      keywords: null,
      blog_category_id: 0,
    });

    merge(
      this.form.get("blog_category_id")?.valueChanges as Observable<any>,
      this.form
        .get("keywords")
        ?.valueChanges.pipe(debounceTime(500)) as Observable<any>,
    )
      .pipe(debounceTime(500))
      .subscribe((value) => {
        this.getMasterData(false);
      });
  }

  _setLoading(value: boolean, time: number = 500) {
    setTimeout(() => {
      this.isLoading.update((_) => value);
    }, time);
  }

  getMasterData(reset = true) {
    const params = this.form.getRawValue();
    if (params.blog_category_id === 0) {
      delete params.blog_category_id;
    }
    if (reset) {
      this._setLoading(true, 0);
    }
    this.tableLoading.update((_) => true);

    forkJoin([
      this.#blogService.getBlogs(params) as Observable<any>,
      this.#blogService.getBlogCategories() as Observable<any>,
    ]).subscribe({
      next: ([blogs, blog_categories]) => {
        this.blogCategories = [{ id: 0, name: "ทั้งหมด" }, ...blog_categories];
        blogs.data = this.#blogService.mapImageInnerHtml(blogs.data);

        this.blogs = blogs.data;
      },
      // `error` on a subscribe object does not also fire `complete` — without
      // this the loading flags this sets true at the top of the method never
      // came back down, so a failed request left the whole page on its
      // skeleton forever with nothing on screen to say why.
      error: (error) => {
        this._setLoading(false);
        this.tableLoading.update((_) => false);
        this.#toastService.toastError(this.#translate.instant("APP.LOAD_ERROR"));
      },
      complete: () => {
        this._setLoading(false);
        this.tableLoading.update((_) => false);
      },
    });
  }

  async fetchBlogList() {
    this.tableLoading.update((_) => true);
    let payload = this.form.getRawValue();
    try {
      const blogFiltered = await lastValueFrom(
        this.#blogService.getBlogs(payload),
      );
      this.dataSource = blogFiltered;
      setTimeout(() => {
        this.tableLoading.update((_) => false);
      }, 500);
    } catch (error) {
      // Same trap as above: the empty catch left `tableLoading` stuck true —
      // a search or filter that failed showed the skeleton forever.
      this.tableLoading.update((_) => false);
      this.#toastService.toastError(this.#translate.instant("APP.LOAD_ERROR"));
    }
  }

  onRowClick(row: any) {
    this.#router.navigate([`courses/${row.id}`]);
  }
  onTableChange(event: Event) {
    this.form.patchValue(event);
  }
}
